import React, { useState, useEffect } from "react";
import Modal from "@mui/material/Modal";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import { TextField, Tooltip, FormControl, InputLabel, Select, MenuItem } from "@mui/material";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import EditIcon from "@mui/icons-material/Edit";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { Handle, Position } from "reactflow";
import { HandleDot } from "../handle";
import { useTheme } from "../../../contexts/ThemeContext";

export const DelayNodeEditModal = ({ open, handleClose, data }) => {
  const { darkMode } = useTheme();
  const isEdit = !!data?.id;

  const currentConfig = data?.data?.config || {};
  const [mode, setMode] = useState(currentConfig.mode || "duration");
  const [minutes, setMinutes] = useState(
    currentConfig.durationMinutes
      ? currentConfig.durationMinutes % 60
      : data?.data?.min || 0
  );
  const [hours, setHours] = useState(
    currentConfig.durationMinutes
      ? Math.floor((currentConfig.durationMinutes % 1440) / 60)
      : data?.data?.hour || 0
  );
  const [days, setDays] = useState(
    currentConfig.durationMinutes
      ? Math.floor(currentConfig.durationMinutes / 1440)
      : data?.data?.days || 0
  );
  const [untilTime, setUntilTime] = useState(currentConfig.untilTime || "09:00");

  useEffect(() => {
    if (data?.data?.config) {
      const cfg = data.data.config;
      setMode(cfg.mode || "duration");
      if (cfg.durationMinutes !== undefined) {
        const total = Number(cfg.durationMinutes) || 0;
        setDays(Math.floor(total / 1440));
        setHours(Math.floor((total % 1440) / 60));
        setMinutes(total % 60);
      }
      setUntilTime(cfg.untilTime || "09:00");
    } else if (data?.data) {
      setMinutes(Number(data.data.min) || 0);
      setHours(Number(data.data.hour) || 0);
      setDays(Number(data.data.days) || 0);
    }
  }, [data]);

  const onSave = () => {
    const totalMinutes = Number(days) * 1440 + Number(hours) * 60 + Number(minutes);
    const config = {
      mode,
      durationMinutes: mode === "duration" ? Math.max(1, totalMinutes) : undefined,
      untilTime: mode === "until_time" ? untilTime : undefined,
    };
    handleClose(
      {
        config,
        min: minutes,
        hour: hours,
        days: days,
      },
      isEdit
    );
  };

  const modalStyle = {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: 480,
    maxWidth: "95vw",
    bgcolor: darkMode ? "#18181b" : "#ffffff",
    border: `1px solid ${darkMode ? "#27272a" : "#e2e8f0"}`,
    borderRadius: 3,
    boxShadow: 24,
    p: 3.5,
  };

  return (
    <Modal open={open} onClose={() => handleClose(null)}>
      <Box sx={modalStyle}>
        <div className="flex items-center gap-2 mb-2">
          <div className="p-2 rounded-lg bg-teal-500/10 text-teal-500">
            <AccessTimeIcon />
          </div>
          <div>
            <h2 className={`text-lg font-bold ${darkMode ? "text-white" : "text-slate-900"}`}>
              {isEdit ? "Edit Delay Node" : "Configure Delay"}
            </h2>
            <p className={`text-xs ${darkMode ? "text-zinc-400" : "text-slate-500"}`}>
              Suspends the workflow journey for a duration or until a specified time window.
            </p>
          </div>
        </div>

        <div className="space-y-4 my-4">
          <FormControl fullWidth size="small">
            <InputLabel id="delay-mode-label">Delay Mode</InputLabel>
            <Select
              labelId="delay-mode-label"
              value={mode}
              label="Delay Mode"
              onChange={(e) => setMode(e.target.value)}
            >
              <MenuItem value="duration">Specific Duration</MenuItem>
              <MenuItem value="until_time">Wait Until Time of Day (e.g. 09:00)</MenuItem>
              <MenuItem value="until_business_hours">Wait Until Next Business Hours Window</MenuItem>
            </Select>
          </FormControl>

          {mode === "duration" && (
            <div className="grid grid-cols-3 gap-3">
              <TextField
                size="small"
                type="number"
                label="Days"
                value={days}
                onChange={(e) => setDays(Math.max(0, Number(e.target.value)))}
                inputProps={{ min: 0 }}
              />
              <TextField
                size="small"
                type="number"
                label="Hours"
                value={hours}
                onChange={(e) => setHours(Math.max(0, Math.min(23, Number(e.target.value))))}
                inputProps={{ min: 0, max: 23 }}
              />
              <TextField
                size="small"
                type="number"
                label="Minutes"
                value={minutes}
                onChange={(e) => setMinutes(Math.max(0, Math.min(59, Number(e.target.value))))}
                inputProps={{ min: 0, max: 59 }}
              />
            </div>
          )}

          {mode === "until_time" && (
            <TextField
              fullWidth
              size="small"
              label="Time of Day (24-hour HH:MM)"
              value={untilTime}
              onChange={(e) => setUntilTime(e.target.value)}
              placeholder="09:00"
              helperText="E.g. 09:00 for 9:00 AM in workflow timezone"
            />
          )}

          {mode === "until_business_hours" && (
            <div className={`p-3 rounded-lg text-xs ${darkMode ? "bg-zinc-800 text-zinc-300" : "bg-slate-100 text-slate-700"}`}>
              The contact will wait until the workflow's configured quiet hours window opens (default 09:00 - 20:00).
            </div>
          )}
        </div>

        <div className="flex justify-end gap-2 pt-3 border-t border-slate-200 dark:border-zinc-700">
          <Button variant="outlined" onClick={() => handleClose(null)}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={onSave}
            style={{ backgroundColor: "#0d9488" }}
          >
            {isEdit ? "Update Node" : "Add Node"}
          </Button>
        </div>
      </Box>
    </Modal>
  );
};

export const DelayNode = (props) => {
  const { data, selected, onEdit, onAdd, onDeleteNode, edges = [], ...rest } = props;
  const { darkMode } = useTheme();

  const cfg = data?.config || {};
  const mode = cfg.mode || "duration";

  let displayText = "Wait";
  if (mode === "until_time") {
    displayText = `Until ${cfg.untilTime || "09:00"}`;
  } else if (mode === "until_business_hours") {
    displayText = "Until Business Hours";
  } else {
    const totalMin = cfg.durationMinutes || ((data?.days || 0) * 1440 + (data?.hour || 0) * 60 + (data?.min || 0));
    const d = Math.floor(totalMin / 1440);
    const h = Math.floor((totalMin % 1440) / 60);
    const m = totalMin % 60;
    const parts = [];
    if (d > 0) parts.push(`${d}d`);
    if (h > 0) parts.push(`${h}h`);
    if (m > 0 || parts.length === 0) parts.push(`${m}m`);
    displayText = `Wait ${parts.join(" ")}`;
  }

  const isConnected = edges.some((e) => e.source === rest.id);

  return (
    <div
      className={`w-48 shadow-lg rounded-xl overflow-hidden transition-all duration-200 ${
        darkMode ? "bg-[#18181b] border border-zinc-700" : "bg-white border border-slate-200"
      } ${
        selected ? "ring-2 ring-teal-500 shadow-teal-500/20" : "hover:border-teal-400"
      }`}
    >
      <Handle id="target" type="target" position={Position.Left} />

      <div className="flex items-center justify-between px-3 py-2 bg-gradient-to-r from-teal-600 to-emerald-600 text-white">
        <div className="flex items-center gap-1.5 min-w-0">
          <AccessTimeIcon style={{ fontSize: 16 }} />
          <span className="text-xs font-semibold tracking-wide uppercase truncate">
            Delay
          </span>
        </div>
        <div className="flex items-center gap-1">
          <Tooltip title="Edit Delay">
            <EditIcon
              style={{ fontSize: 14, cursor: "pointer" }}
              onClick={() => onEdit(props)}
            />
          </Tooltip>
          {onDeleteNode && rest.id !== "1" && (
            <Tooltip title="Delete Node">
              <DeleteOutlineIcon
                style={{ fontSize: 15, cursor: "pointer" }}
                onClick={() => onDeleteNode(rest.id)}
              />
            </Tooltip>
          )}
        </div>
      </div>

      <div className="p-3">
        <div className={`text-xs font-semibold ${darkMode ? "text-zinc-100" : "text-slate-800"}`}>
          {displayText}
        </div>
        <div className={`text-[10px] mt-0.5 ${darkMode ? "text-zinc-400" : "text-slate-500"}`}>
          Mode: {mode.replace(/_/g, " ")}
        </div>
      </div>

      <HandleDot
        addVisible={!isConnected}
        id="next"
        type="source"
        position={Position.Right}
        onAdd={(v) =>
          onAdd(v, rest, rest.id, {
            type: "DELAY",
            key: "next",
          })
        }
      />
    </div>
  );
};
